import { Link, useParams } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { Figure } from "../components/Figure";
import { ROUTES } from "../lib/routes";
import { CENTERS, getCenter } from "../lib/researchCenters";
import type { Patent } from "../lib/researchCenters";
import { getStaffMember, initials } from "../lib/staff";
import { useScrollPastHero } from "../lib/useScrollPastHero";

/** Counts are null where the centre returned "Nil" — show a dash, never a 0. */
const num = (n: number | null) => (n == null ? "—" : String(n));

/**
 * "15 records · 4 granted · 11 published, awaiting grant". A patent carrying a
 * grant date counts as granted; the rest are published and still under
 * examination, which is how the EE-RD return describes them.
 */
function patentSummary(patents: Patent[]): string {
  const granted = patents.filter((p) => p.granted).length;
  const pending = patents.length - granted;
  const parts = [`${patents.length} record${patents.length === 1 ? "" : "s"}`];
  if (granted) parts.push(`${granted} granted`);
  if (pending) parts.push(`${pending} published, awaiting grant`);
  return parts.join(" · ");
}

export default function ResearchCenter() {
  const { slug = "" } = useParams<{ slug: string }>();
  const c = getCenter(slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!c) {
    return (
      <section className="center-hero">
        <div className="container">
          <div className="ch-eyebrow">404 · Research</div>
          <h1 className="ch-name">Research centre not found.</h1>
          <p style={{ color: "rgba(255,255,255,0.7)" }}>
            <Link to={ROUTES.research} style={{ color: "var(--gold-400)" }}>← Back to research</Link>
          </p>
        </div>
      </section>
    );
  }

  const otherCenters = CENTERS.filter((x) => x.slug !== c.slug).slice(0, 3);
  // A name on a departmental return may have no row in the staff record.
  const facultyMembers = c.facultySlugs
    .map(getStaffMember)
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={`RESEARCH · ${c.short}`}
        title={c.name}
        back={{ label: "All research", to: ROUTES.research }}
      />
      <section className="center-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.research}>Research</Link>
            <span aria-hidden>/</span>
            <span>Centres</span>
          </nav>
          <div className="ch-eyebrow">VTU-recognised research centre · Established {c.estd}</div>
          <span className="ch-short">{c.short}</span>
          <h1 className="ch-name">{c.name}</h1>
          <p className="ch-summary">{c.desc}</p>

          <div className="ch-stats">
            <div>
              <div className="num">{num(c.scholars)}</div>
              <div className="lbl">Active scholars</div>
            </div>
            <div>
              <div className="num">{num(c.projects)}</div>
              <div className="lbl">Funded projects</div>
            </div>
            <div>
              <div className="num">{num(c.pubs)}</div>
              <div className="lbl">Publications · 5y{c.pubsNote ? ` · ${c.pubsNote}` : ""}</div>
            </div>
            <div>
              <div className="num">{c.estd}</div>
              <div className="lbl">Established</div>
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body is-wide">
              {c.intro.length > 0 && (
                <>
                  <h2>About the centre</h2>
                  {c.intro.map((p, i) => <p key={i}>{p}</p>)}
                </>
              )}

              {c.focus.length > 0 && (
                <>
                  <h2>Focus areas</h2>
                  <div className="center-focus">
                    {c.focus.map((f, i) => <span key={i}>{f}</span>)}
                  </div>
                </>
              )}

              {c.ongoing.length > 0 && (
                <>
                  <h2>Projects</h2>
                  <div className="center-table-scroll">
                  <table className="center-projects">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Principal investigator</th>
                        <th>Funder</th>
                        <th>Outlay</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.ongoing.map((p, i) => (
                        <tr key={i}>
                          <td className="tt">{p.title}</td>
                          <td>{p.pi || "—"}</td>
                          <td>{p.funder}</td>
                          <td className="amt">{p.amount || "—"}</td>
                          <td><span className={`proj-status is-${p.status}`}>{p.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </>
              )}

              {facultyMembers.length > 0 && (
                <>
                  <h2>Faculty associated with the centre</h2>
                  <div className="center-faculty">
                    {facultyMembers.map((f) => (
                      <div key={f.slug} className="fa-card">
                        <Figure
                          media={f.photo}
                          fallbackLabel={initials(f.name)}
                          aspect="1/1"
                          className="fa-photo"
                          sizes="38px"
                        />
                        <div className="fa-info">
                          <span className="fa-name">{f.name}</span>
                          <span className="fa-role">{f.designation}{f.deptName ? ` · ${f.deptName}` : ""}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {c.patents.length > 0 && (
                <>
                  <h2>Patents filed &amp; granted</h2>
                  <p className="meta">{patentSummary(c.patents)}</p>
                  <ul className="center-patents">
                    {c.patents.map((p, i) => (
                      <li key={i}>
                        <span className="cp-title">{p.title}</span>
                        <span className={`proj-status is-${p.granted ? "ongoing" : "completed"}`}>
                          {p.granted ? "granted" : "published"}
                        </span>
                        <span className="cp-people">{p.faculty}</span>
                        <span className="cp-meta">
                          <span>{p.office}</span>
                          <span className="cp-num">{p.number}</span>
                          {p.published && <span>published {p.published}</span>}
                          {p.granted && <span>granted {p.granted}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {c.highlights.length > 0 && (
                <>
                  <h2>Highlights</h2>
                  <ul className="detail-list">
                    {c.highlights.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </>
              )}

              {c.partners.length > 0 && (
                <>
                  <h2>Funders &amp; partners</h2>
                  <div className="center-focus">
                    {c.partners.map((p, i) => <span key={i}>{p}</span>)}
                  </div>
                </>
              )}
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Short name</div>
                <div className="dmc-value">{c.short}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Coordinator</div>
                <div className="dmc-value">{c.contact.coordinator}</div>
              </div>
              {c.contact.room && (
                <div className="dmc-block">
                  <div className="dmc-label">Location</div>
                  <div className="dmc-value">{c.contact.room}</div>
                </div>
              )}
              <div className="dmc-block">
                <div className="dmc-label">Email</div>
                <div className="dmc-value"><a href={`mailto:${c.contact.email}`}>{c.contact.email}</a></div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Established</div>
                <div className="dmc-value">{c.estd}</div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {otherCenters.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">Across the institute</div>
              <h2>Other research centres</h2>
            </div>
            <div className="er-grid">
              {otherCenters.map((o) => (
                <Link key={o.slug} to={`${ROUTES.researchCenter}/${o.slug}`} className="er-card">
                  <div className="er-date">
                    <span className="er-day">{num(o.scholars)}</span>
                    <span className="er-month">SCHOLARS</span>
                  </div>
                  <div className="er-body">
                    <span className="er-tag">{o.short}</span>
                    <h3>{o.name}</h3>
                    <span className="er-venue">{num(o.pubs)} pubs · {num(o.projects)} projects</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
